import React, { useEffect, useMemo, useState } from 'react'
import LoadingSpinner from 'Components/LoadingSpinner'
import { ListLayout } from 'Components/Layouts'
import { useTranslation } from 'react-i18next'
import LargeScreen from './LargeScreen'
import SmallScreen from './SmallScreen'
import WithholdingEmailSendModal from 'Components/Modals/WithholdingEmailSendModal'
import Filter from './Filter'
import WithholdingsListContext from './WithholdingsListContext'
import { useWithholdingsQuery } from 'hooks/useWithholdingsQuery'
import { currentMonthCivilRange } from 'Utilities/dateTime'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Service from 'Service'
import useStores from 'hooks/useStores'
import { withholdingsQueryKey } from 'hooks/queryKeys'
import { WITHHOLDINGS_LIST } from 'Constants/Routes'

const defaultRange = currentMonthCivilRange

const WithholdingListContent = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const history = useHistory()
  const { companyStore } = useStores()
  const companyKey = companyStore.selectedCompany?.Keycompany
  const [range, setRange] = useState(defaultRange)

  const highlightId = useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('id') || ''
  }, [location.search])

  const dateQuery = useWithholdingsQuery(range.from, range.to)
  const allQuery = useQuery({
    queryKey: [...withholdingsQueryKey(companyKey, 'all', 'all', 0, 500), 'highlight', highlightId],
    queryFn: async () => {
      const data = await Service.Withholding.getAll(0, 500)
      return Array.isArray(data) ? data : []
    },
    enabled: Boolean(companyKey && highlightId),
  })

  const isLoading = highlightId ? allQuery.isLoading : dateQuery.isLoading
  const withholdings = highlightId ? (allQuery.data || []) : (dateQuery.data || [])

  useEffect(() => {
    if (!highlightId || isLoading) return
    const timer = window.setTimeout(() => {
      const row = document.getElementById(`withholding-row-${highlightId}`)
      row?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 80)
    return () => window.clearTimeout(timer)
  }, [highlightId, isLoading, withholdings])

  return (
    <WithholdingsListContext.Provider value={{ withholdings, isLoading, highlightId }}>
      <Filter
        isLoading={isLoading}
        defaultFrom={range.from}
        defaultTo={range.to}
        onSearch={(dateFrom, dateTo) => {
          setRange({ from: dateFrom, to: dateTo })
          if (highlightId) history.replace(WITHHOLDINGS_LIST)
        }}
      />

      {isLoading && <LoadingSpinner />}

      {!isLoading && (
        <>
          {withholdings.length === 0 && <h2>{t('not_withholdins_found')}</h2>}
          {withholdings.length > 0 && (
            <>
              {highlightId && !withholdings.some(w => w.Id === highlightId) && (
                <p className="text-warning">
                  {t('withholding_not_found_in_list', {
                    defaultValue: 'No se encontró la retención indicada en la lista.',
                  })}
                </p>
              )}
              <LargeScreen />
              <SmallScreen />
              <WithholdingEmailSendModal />
            </>
          )}
        </>
      )}
    </WithholdingsListContext.Provider>
  )
}

export default () => (
  <ListLayout title="withholdings">
    <WithholdingListContent />
  </ListLayout>
)
